import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { useApiClients } from './ApiClientsProvider';
import { apiKeys } from './keys';
import { unwrap } from './result';
import { useServiceMutation, type ServiceMutation } from './useServiceMutation';

/**
 * The upgrade to a newer tariff level (SDD-016 §5), on the INSTANCE API — a policy surface never
 * reads the configuration API (OQ-11), so the candidates come from here too. Policy AND group
 * policy, one hook per operation with a `kind` switch.
 *
 * The dry run is a POST that writes nothing, and it still re-reads the instance: the SDK's ETag
 * middleware drops a resource's parked ETag after ANY successful non-safe request, so without the
 * re-read the confirm that follows would carry no If-Match and come back 428 — the same reason
 * `useValidateTarifVersion` re-reads the generation.
 */

export type UpgradeKind = 'policy' | 'group';

export type UpgradeTarget = InstanceApi.UpgradeTarget;
export type UpgradeCheckResult = InstanceApi.UpgradeCheckResult;

const instanceKey = (kind: UpgradeKind, code: string) =>
  kind === 'policy' ? apiKeys.instance.policy(code) : apiKeys.instance.groupPolicy(code);

const targetsKey = (kind: UpgradeKind, code: string) => [...instanceKey(kind, code), 'upgrade-targets'] as const;

/** The versions an instance may be upgraded to: PUBLISHED or ACTIVE, newer, same generation. */
export function useUpgradeTargets(
  kind: UpgradeKind,
  code: string,
  enabled = true,
): UseQueryResult<readonly UpgradeTarget[]> {
  const { instance } = useApiClients();
  return useQuery({
    queryKey: targetsKey(kind, code),
    enabled,
    queryFn: async (): Promise<readonly UpgradeTarget[]> => {
      const response =
        kind === 'policy'
          ? await unwrap(instance.client.GET('/v1/policies/{code}/upgrade-targets', { params: { path: { code } } }))
          : await unwrap(
              instance.client.GET('/v1/group-policies/{code}/upgrade-targets', { params: { path: { code } } }),
            );
      return response.targets ?? [];
    },
  });
}

export interface UpgradeVariables {
  readonly kind: UpgradeKind;
  readonly code: string;
  readonly target: string;
  /** RFC 3339 full-date: the day the new tariff level takes effect. */
  readonly effectiveFrom: string;
}

/** The dry run: every check, nothing written. */
export function useCheckUpgrade(): ServiceMutation<UpgradeVariables, UpgradeCheckResult> {
  const { instance } = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ kind, code, target, effectiveFrom }: UpgradeVariables) => {
      const response =
        kind === 'policy'
          ? await unwrap(
              instance.client.POST('/v1/policies/{code}:checkUpgrade', {
                params: { path: { code } },
                body: { targetTarifVersionErn: target, effectiveFrom },
              }),
            )
          : await unwrap(
              instance.client.POST('/v1/group-policies/{code}:checkUpgrade', {
                params: { path: { code } },
                body: { targetGroupTarifVersionErn: target, effectiveFrom },
              }),
            );
      return response.result ?? {};
    },
    // Writes nothing; the re-read restores the ETag the confirm will quote (see the file comment).
    invalidates: ({ kind, code }) => [instanceKey(kind, code)],
  });
}

export type UpgradeResult = InstanceApi.UpgradePolicyResponse | InstanceApi.UpgradeGroupPolicyResponse;

/** The write — one endorsement of THIS instance. A group upgrade moves no member (SSOT §5.4). */
export function useUpgrade(): ServiceMutation<UpgradeVariables, UpgradeResult> {
  const { instance } = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ kind, code, target, effectiveFrom }: UpgradeVariables) =>
      kind === 'policy'
        ? unwrap(
            instance.client.POST('/v1/policies/{code}:upgrade', {
              params: { path: { code } },
              body: { targetTarifVersionErn: target, effectiveFrom },
            }),
          )
        : unwrap(
            instance.client.POST('/v1/group-policies/{code}:upgrade', {
              params: { path: { code } },
              body: { targetGroupTarifVersionErn: target, effectiveFrom },
            }),
          ),
    // This instance, its timeline and its list — never the other side of the parent/child line.
    invalidates: ({ kind, code }) =>
      kind === 'policy'
        ? [apiKeys.instance.policy(code), apiKeys.instance.policyTimeline(code), apiKeys.instance.policyList()]
        : [
            apiKeys.instance.groupPolicy(code),
            apiKeys.instance.groupPolicyTimeline(code),
            apiKeys.instance.groupPolicyList(),
          ],
  });
}
